"use client";

import { cn, dayjs, formatTitleForUrl } from "utils";

import { Content } from "@features/api/openData";
import { I18nUtil } from "@utils/i18n";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import NextImage from "../NextImage";
import { ROUTER_PATH } from "@config/router";
import Translations from "@translate/components/TokenDetails/News";
import { ViewMoreButton } from "./ViewMoreButton";
import { buildLocale } from "@utils/buildLocale";
import { memo } from "react";

const I18n = I18nUtil.createScoped(Translations);

interface NewsItemProps {
  /** 新闻标题 */
  title?: string;
  /** 新闻描述 */
  content?: string;
  /** 新闻图片URL */
  imageUrl?: string;
  /** 新闻来源名称 */
  // sourceName: string;
  /** 新闻来源图片URL */
  sourceImageUrl?: string;
  /** 新闻链接地址 */
  link?: string;
  /** 容器自定义类名 */
  className?: string;
  time?: number;
  detailLink?: string;
  mediasImageUrl?: string;
}

/**
 * 新闻项组件
 */
const NewsItem = memo(
  ({
    title,
    content,
    imageUrl,
    time,
    detailLink,
    className,
  }: NewsItemProps) => {
    const ContentWrapper = ({
      children,
      className,
    }: {
      children: React.ReactNode;
      className?: string;
    }) =>
      detailLink ? (
        <ShellLink
          aria-label="Content Wrapper"
          data-openreplay-label="market_contentwrapper_open"
          id="lnk-content-wrapper"
          href={detailLink}
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </ShellLink>
      ) : (
        <div className={className}>{children}</div>
      );

    return (
      <ContentWrapper
        className={cn(
          "flex gap-4 md:gap-5 items-center cursor-pointer flex-row-reverse group",
          className,
        )}
      >
        {/* 新闻图片，仅在有图片时显示 */}
        {imageUrl && (
          <div className="md:w-65 md:h-36.5 w-30 h-17 overflow-hidden flex-shrink-0 rounded-2">
            <NextImage
              src={imageUrl}
              alt={title}
              width={260}
              height={146}
              className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.2]"
            />
          </div>
        )}

        {/* 新闻文本内容 */}
        <div className="flex-1 flex flex-col gap-2 md:gap-3 justify-between max-w-[440px]">
          {/* 标题和描述嵌套在一个父节点中 */}
          <div className="flex flex-1 flex-col gap-2">
            {/* 标题 */}
            <h3
              aria-label="Content Wrapper h3"
              data-openreplay-label="market_contentwrapper_h3"
              id="sem-content-wrapper-h3"
              className="body_medium_l text-content_primary line-clamp-2"
            >
              {title}
            </h3>

            {/* 描述，在h5上隐藏 */}
            <p className="body_m text-content_secondary md:line-clamp-3 hidden">
              {content}
            </p>
          </div>
          {/* 发布时间 */}
          <span className="body_s text-content_tertiary">
            {dayjs(time).format("MMM DD YYYY, HH:mm")}
          </span>
          {/* 新闻来源信息 */}
          {/* <div className="w-fit flex items-center p-1 rounded-0.5 bg-background_white_overlay10 backdrop-blur-10 gap-1">
            {sourceImageUrl && (
              <div className="w-3 h-3 rounded-full overflow-hidden">
                <Image
                  src={sourceImageUrl}
                  alt={sourceName}
                  width={12}
                  height={12}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <span
              className="body_s text-content_tertiary flex items-center origin-left"
              style={{
                transform: "scale(0.833)",
                transformOrigin: "left center",
              }}
            >
              {sourceName}
            </span>
          </div> */}
        </div>
      </ContentWrapper>
    );
  },
);

NewsItem.displayName = "NewsItem";

interface NewsProps {
  /** 新闻数据 */
  news: Content[];
  /** 查看更多按钮文本 */
  viewMoreLabel?: string;
  /** 查看更多按钮点击事件 */
  onViewMore?: () => void;
  /** 自定义类名 */
  className?: string;
  /** 币种名称 */
  symbol?: string;
  /** 语言 */
  locale?: string;
  /** 分类 */
  category?: string;
}

/**
 * 新闻组件
 */
const News = memo(
  ({
    news,
    viewMoreLabel = I18n.t("View More"),
    onViewMore,
    className,
    locale,
    category: _category,
  }: NewsProps) => {
    // 如果没有新闻数据，则不渲染
    if (!news || news.length === 0) {
      return null;
    }

    return (
      <div className={cn("w-full flex flex-col gap-4", className)}>
        {/* 标题 */}
        <div className="flex justify-between items-center">
          <h2
            aria-label="Content Wrapper h2"
            data-openreplay-label="market_contentwrapper_h2"
            id="sem-content-wrapper-h2"
            className="headline_regular_s text-content_primary"
          >
            {I18n.t("Related News")}
          </h2>
        </div>
        {/* 新闻列表 */}
        <div className="w-full flex flex-col gap-3 md:gap-6">
          {news.map((item, index) => {
            const urlPrefix = buildLocale(locale);
            const detailUrl =
              `${urlPrefix}${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formatTitleForUrl(item?.source_link || "")}` ||
              "";
            return (
              <NewsItem
                key={index}
                title={item.title}
                content={item.description}
                imageUrl={item.medias?.[0]?.url}
                time={item.issue_date}
                detailLink={detailUrl}
              />
            );
          })}
        </div>
        {/* 查看更多按钮 - 底部居中 */}
        {onViewMore && (
          <div className="flex justify-center">
            <ViewMoreButton
              data-openreplay-label="market_news_open"
              label={viewMoreLabel}
              className="flex-row-reverse"
              onClick={onViewMore}
            />
          </div>
        )}
      </div>
    );
  },
);

News.displayName = "News";

export { News, NewsItem };
export type { NewsProps, NewsItemProps };
